import {Location} from '@angular/common';
import {HttpBackend, HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable, NgZone} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {
  LAST_HEARTBEAT_KEY, REOPEN_GRACE_KEY, TAB_CHANNEL_NAME, TAB_PONG_WAIT_MS, UNLOCK_ROUTE,
  clearReopenLocked, expireHeartbeat, hasDeviceSession, isReopenExpired, isReverifyRequiredResponse, isSameTabReload,
  isReopenReported, markPagehide, markReopenReported, readNavigation, recordHeartbeat, reopenLockedAt, safeReturnUrl,
  setReopenLocked, shouldLockNow, shouldSendHeartbeat, REOPEN_LOCK_ANSWER_GRACE_MS,
  backOffReopenReport, clearReopenDeciding, rateLimitReopenReport, reopenDecidingUntil, reopenRetryAt, setReopenDeciding,
  unlockNavigationFor, webSessionHeaders
} from '../auth/models/web-session.util';

const CHECK_INTERVAL_MS = 15000;
const PING = 'ping';
const PONG = 'pong';

/**
 * How the start-up decision ended: 'none' - nothing to ask (no device session,
 * or a same-tab reload: the heartbeat rule alone applies), 'locked' - the
 * browser was opened (no answer, or nobody to ask), 'undone' - another open
 * tab answered and the reopen lock was taken back.
 */
export type ReopenDecision = 'none' | 'locked' | 'undone';

/**
 * JS-1396 — passkey on browser reopen. While a tab is open it heartbeats the
 * api (shared across tabs through localStorage), so the device session stays
 * alive however long the user is away from the keyboard. A visit after no
 * heartbeat for longer than the reopen grace — the browser was closed, or the
 * machine slept — goes to the unlock screen before any data loads.
 *
 * Every browser open: a start that is not a same-tab reload locks at once
 * and asks the other tabs over a BroadcastChannel; an answer within
 * TAB_PONG_WAIT_MS means another tab is open and the lock is undone, silence
 * means the browser was just opened and the shared reopenLocked flag holds
 * every tab on the unlock screen, and the api is told (passkey/reopen) to
 * mark this device session stale, so it answers 423 until the unlock.
 *
 * Without BroadcastChannel (an old browser, or a blocked constructor) no tab
 * can be asked, so every new tab is treated as a browser open: it prompts
 * and reports passkey/reopen, so the api marks the device session stale and
 * every open tab locks too (its next request gets 423). Fails closed.
 *
 * The report is not left to the deciding tab alone: until the api has
 * accepted it (REOPEN_REPORTED_KEY), any tab that sees the reopen lock - a
 * reload of the deciding tab inside its answer window, the next tab, the
 * next check() - sends it.
 */
@Injectable({providedIn: 'root'})
export class IdleLockService {
  private started = false;
  // Built on HttpBackend, not the app's HttpClient: ErrorInterceptor injects
  // this service, so HttpClient here would be a DI cycle. The heartbeat
  // attaches its headers by hand and handles its own 423 / 401.
  private rawHttp: HttpClient;
  // Every-open decision: a tab vouches for others only once it has decided,
  // is not itself locked and has been unlocked in the app (`vouchable`), so
  // tabs restored together all lock and a tab on a sign-in screen never answers.
  private decided = false;
  private selfLocked = false;
  private vouchable = false;
  private reportInFlight = false;
  private reportTimer: any = null;
  private reportTimerDue = 0;
  // A 401 on passkey/reopen: the session is gone - full sign-in, no more reports.
  private reportStopped = false;
  private awaitingPong = false;
  private pongTimer: any = null;
  private pendingReturnUrl: string | null = null;
  private channel: any = null;
  private resolveDecision: (decision: ReopenDecision) => void;
  private readonly decision: Promise<ReopenDecision> = new Promise(resolve => this.resolveDecision = resolve);

  constructor(private router: Router, private zone: NgZone, private location: Location, httpBackend: HttpBackend) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  /**
   * True until the start-up decision is final - before start(), and for up to
   * TAB_PONG_WAIT_MS while a new tab asks the others. The unlock screen holds
   * its automatic passkey prompt until then: a pong would take the lock back,
   * and an OS dialog would have flashed for nothing.
   */
  get decisionPending(): boolean {
    return !this.decided;
  }

  /** Resolves once, with how the start-up decision ended. */
  whenDecided(): Promise<ReopenDecision> {
    return this.decision;
  }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.openChannel();
    this.zone.runOutsideAngular(() => {
      setInterval(() => this.check(), CHECK_INTERVAL_MS);
      // The interval alone leaves up to 15 s of stale data after a wake, a
      // tab coming back to the front, or a back/forward-cache restore.
      window.addEventListener('pageshow', () => this.check());
      window.addEventListener('focus', () => this.check());
      // Lets the next document of this tab recognise a reload.
      window.addEventListener('pagehide', () => {
        markPagehide(Date.now());
        // Reloaded / closed while asking: this tab will never decide, so the
        // others need not wait REOPEN_DECIDING_STALE_MS for it.
        if (this.awaitingPong) {
          clearReopenDeciding();
        }
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.check();
        }
      });
    });
    // The server session stays live under a reopen lock: Back, or any link
    // away from the unlock screen, must go straight back to it.
    if (this.router.events) {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.onNavigationEnd(event);
        }
      });
    }
    this.decideAtStart();
  }

  /**
   * Show the unlock screen. Safe to call repeatedly. The device token is
   * kept: the unlock revives that same session on the api.
   */
  lock(): void {
    this.lockAndGetReturnUrl();
  }

  /** lock(), returning the returnUrl it navigated with (null: no navigation). */
  private lockAndGetReturnUrl(): string | null {
    const returnUrl = unlockNavigationFor(this.currentUrl());
    if (returnUrl === null) {
      return null;
    }
    this.noteLocked();
    this.router.navigate([UNLOCK_ROUTE], {queryParams: {returnUrl}});
    return returnUrl;
  }

  private noteLocked(): void {
    this.selfLocked = true;
    this.vouchable = false;
  }

  /**
   * ReopenLockGuard: must a navigation be sent to the unlock screen instead?
   * Same rule as check(), so a tab that is open and unlocked is not locked by
   * the flag a new tab has just set while asking. Records the lock.
   */
  mustLockBeforeActivation(now: number): boolean {
    const lock = shouldLockNow(now, this.isUnlockedInApp());
    if (lock) {
      this.noteLocked();
    }
    return lock;
  }

  // Before the initial navigation router.url is still '/': use the browser's
  // real location so a deep link survives the lock at app start.
  private currentUrl(): string {
    return this.router.navigated ? this.router.url : this.location.path();
  }

  /**
   * Browser opened, or a reload / another tab? On every start with a
   * device session, whatever the route - a browser opened onto a sign-in,
   * reset or unlock screen must still lock, or moving into the app from
   * there would skip the passkey. Runs from AppComponent, before the
   * initial navigation (and so before e.g. LoginGuard's redirect lands).
   * A same-tab reload is known from Navigation Timing; anything else asks
   * the other tabs. Only the navigation differs: an app screen goes to the
   * unlock screen at once, a no-lock screen stays where it is (the shared
   * flag, ReopenLockGuard and the api's 423 hold the rest).
   */
  private decideAtStart(): void {
    const perf = typeof window !== 'undefined' ? (window as any).performance : undefined;
    const session = hasDeviceSession();
    const lockable = unlockNavigationFor(this.currentUrl()) !== null;
    if (!session || isSameTabReload(readNavigation(perf))) {
      this.finishDecision('none');
      // Reload / another tab: today's heartbeat rule (and a reopen lock set
      // by another tab) still apply.
      this.check();
      // A same-tab reload in the app that check() did not lock is unlocked
      // in the app. A sign-in screen never is (it may not answer pings).
      this.vouchable = session && lockable && !this.selfLocked;
      return;
    }
    // Lock first - no data on screen while the other tabs are asked. On a
    // no-lock screen only the flag is set (no navigation).
    setReopenLocked();
    const returnUrl = lockable ? this.zone.run(() => this.lockAndGetReturnUrl()) : null;
    if (!this.channel) {
      // No BroadcastChannel: nobody can answer, so the browser was opened.
      // In such a browser every new tab reports a reopen, so every open
      // tab locks as well (fails closed).
      this.finishDecision('locked');
      this.reportReopen();
      return;
    }
    this.pendingReturnUrl = returnUrl;
    this.awaitingPong = true;
    // Tell the other tabs a live asker exists, so none reports under it.
    setReopenDeciding(Date.now());
    this.channel.postMessage(PING);
    this.zone.runOutsideAngular(() => {
      this.pongTimer = setTimeout(() => {
        // Silence: the browser was opened. Stay locked.
        this.awaitingPong = false;
        this.pongTimer = null;
        clearReopenDeciding();
        this.finishDecision('locked');
        this.reportReopen();
      }, TAB_PONG_WAIT_MS);
    });
  }

  /** Another open tab answered: this was not a browser open. */
  private undoReopenLock(): void {
    this.awaitingPong = false;
    if (this.pongTimer !== null) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    clearReopenLocked();
    this.selfLocked = false;
    this.vouchable = true;
    this.finishDecision('undone');
    // Started on a no-lock screen: nothing to undo but the flag - unless a
    // navigation meanwhile (LoginGuard's redirect) was sent to the unlock
    // screen by ReopenLockGuard; then go where it was going.
    const returnUrl = this.pendingReturnUrl !== null ? this.pendingReturnUrl : this.returnUrlOfUnlockScreen();
    this.pendingReturnUrl = null;
    if (returnUrl !== null) {
      this.zone.run(() => this.router.navigateByUrl(returnUrl));
    }
  }

  /** On the unlock screen: its (sanitised) returnUrl; otherwise null. */
  private returnUrlOfUnlockScreen(): string | null {
    const url = this.currentUrl() || '';
    if (!url.startsWith(UNLOCK_ROUTE)) {
      return null;
    }
    const query = url.indexOf('?') >= 0 ? url.slice(url.indexOf('?') + 1) : '';
    let raw: string | null = null;
    try {
      raw = new URLSearchParams(query).get('returnUrl');
    } catch (e) {
      raw = null;
    }
    return safeReturnUrl(raw);
  }

  private finishDecision(decision: ReopenDecision): void {
    this.decided = true;
    this.resolveDecision(decision);
  }

  /**
   * The browser was opened: make the api agree, so the lock does not rest on
   * the front alone (a removed localStorage flag, a race). The device session
   * is marked stale and every data call gets 423 until unlock-verify revives
   * it. Failures are ignored - the front lock still holds.
   */
  private reportReopen(): void {
    if (this.reportInFlight || this.reportStopped || !hasDeviceSession()) {
      return;
    }
    const retryAt = reopenRetryAt();
    if (retryAt !== null && retryAt > Date.now()) {
      // A tab's report failed: everyone waits for the shared backoff.
      this.reportPendingReopen(Date.now());
      return;
    }
    let apiToken: string | null = null;
    try {
      apiToken = JSON.parse(localStorage.getItem('currentUser'))?.api_token || null;
    } catch (e) {
      return;
    }
    if (!apiToken) {
      return;
    }
    const headers = {Authorization: `Bearer ${apiToken}`, ...webSessionHeaders()};
    this.reportInFlight = true;
    this.rawHttp.post(`${environment.apiUrl}/passkey/reopen`, {}, {headers}).subscribe(
      () => {
        // Accepted (2xx): no tab needs to send it again for this lock.
        this.reportInFlight = false;
        markReopenReported();
      },
      (err: HttpErrorResponse) => {
        this.reportInFlight = false;
        const status = err ? err.status : 0;
        const now = Date.now();
        if (status === 401) {
          // Session gone (expired / signed out elsewhere): stop for good and
          // sign in fully - the heartbeat's 401 path. Sign-out clears the
          // lock keys (localStorage.clear()).
          this.reportStopped = true;
          this.zone.run(() => this.router.navigate(['/logout']));
          return;
        }
        if (status === 429) {
          rateLimitReopenReport(now, err && err.headers && typeof err.headers.get === 'function' ? err.headers.get('Retry-After') : null);
        } else {
          backOffReopenReport(now);
        }
        // Not marked: retried once the shared backoff is over.
        this.reportPendingReopen(now);
      }
    );
  }

  /**
   * A reopen lock the api has not accepted yet: report it. Not while this tab
   * is still asking (a pong may undo it), and not before the lock is older
   * than the answer window (another tab may still be asking) - a timer
   * comes back then.
   */
  private reportPendingReopen(now: number): void {
    const at = reopenLockedAt();
    if (at === null || isReopenReported() || this.awaitingPong || this.reportStopped || !hasDeviceSession()) {
      return;
    }
    const answerWindow = TAB_PONG_WAIT_MS + REOPEN_LOCK_ANSWER_GRACE_MS;
    // A lock set "in the future" (clock skew) counts as just set; one beyond
    // the window ahead counts as old, so it can never defer the report for ever.
    const age = at - now > answerWindow ? answerWindow : Math.max(0, now - at);
    let wait = answerWindow - age;
    // A live asking tab (B): wait for it to decide, or to be presumed dead.
    const decidingUntil = reopenDecidingUntil(now);
    if (decidingUntil !== null) {
      wait = Math.max(wait, decidingUntil - now);
    }
    // The shared backoff after a failed report (A).
    const retryAt = reopenRetryAt();
    if (retryAt !== null) {
      wait = Math.max(wait, retryAt - now);
    }
    if (wait <= 0) {
      this.reportReopen();
      return;
    }
    const due = now + wait;
    if (this.reportTimer !== null && this.reportTimerDue <= due) {
      return;   // already coming back at or before that
    }
    if (this.reportTimer !== null) {
      // Recomputed shorter (e.g. the backoff was cleared): do not let the old timer hold it.
      clearTimeout(this.reportTimer);
    }
    this.reportTimerDue = due;
    this.zone.runOutsideAngular(() => {
      this.reportTimer = setTimeout(() => {
        this.reportTimer = null;
        this.reportPendingReopen(Date.now());
      }, wait);
    });
  }

  /** Open and unlocked in the app, on an app screen, with a device session. */
  private isUnlockedInApp(): boolean {
    return this.decided && this.vouchable && !this.selfLocked && !this.awaitingPong && hasDeviceSession()
      && unlockNavigationFor(this.currentUrl()) !== null;
  }

  private openChannel(): void {
    const Channel = typeof window !== 'undefined' ? (window as any).BroadcastChannel : undefined;
    if (typeof Channel !== 'function') {
      return;
    }
    try {
      this.channel = new Channel(TAB_CHANNEL_NAME);
      this.channel.onmessage = (event: any) => this.onTabMessage(event && event.data);
    } catch (e) {
      this.channel = null;
    }
  }

  private onTabMessage(data: any): void {
    if (data === PING) {
      if (this.canVouch()) {
        this.channel.postMessage(PONG);
      }
    } else if (data === PONG && this.awaitingPong) {
      this.undoReopenLock();
    }
  }

  /**
   * This tab's own state, not the shared flag (the asking tab has just set
   * it): unlocked in the app (a same-tab reload check() did not lock, an
   * undone lock, or a navigation into the app while nothing locks), on an
   * app screen by its real location (router.url is '/' before the first
   * navigation), not past the heartbeat grace. Sign-in, reset and unlock
   * screens never answer.
   */
  private canVouch(): boolean {
    if (!this.isUnlockedInApp()) {
      return false;
    }
    try {
      return !isReopenExpired(Date.now(), localStorage.getItem(LAST_HEARTBEAT_KEY), localStorage.getItem(REOPEN_GRACE_KEY));
    } catch (e) {
      return false;
    }
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const url = event.urlAfterRedirects || event.url || '';
    if (this.decided && !this.awaitingPong && unlockNavigationFor(url) !== null && !shouldLockNow(Date.now(), this.isUnlockedInApp())) {
      // In the app while nothing locks (unlocked, undone, or signed in here).
      this.selfLocked = false;
      this.vouchable = hasDeviceSession();
    }
    this.check();
  }

  private check(): void {
    const now = Date.now();
    this.reportPendingReopen(now);
    if (shouldLockNow(now, this.isUnlockedInApp())) {
      this.zone.run(() => this.lock());
      return;
    }
    // Not on the unlock / auth routes: lock() is a no-op there, so a claim
    // made there could never be reconciled with a 423.
    if (shouldSendHeartbeat(now) && unlockNavigationFor(this.router.url) !== null) {
      // Claim the slot first so other tabs (and the next tick) skip it.
      recordHeartbeat(now);
      this.sendHeartbeat(String(now));
    }
  }

  /** One cheap authenticated GET (1 query, 200 for agency and client users). */
  private sendHeartbeat(claimed: string): void {
    let apiToken: string | null = null;
    try {
      apiToken = JSON.parse(localStorage.getItem('currentUser'))?.api_token || null;
    } catch (e) {
      return;
    }
    if (!apiToken) {
      return;
    }
    const headers = {Authorization: `Bearer ${apiToken}`, ...webSessionHeaders()};
    this.rawHttp.get(`${environment.apiUrl}/can-access-functionality`, {headers}).subscribe(
      () => {},
      (err: HttpErrorResponse) => {
        if (isReverifyRequiredResponse(err)) {
          // Make the front agree with the api, or the lock would not stick.
          expireHeartbeat(claimed);
          this.zone.run(() => this.lock());
        } else if (err && err.status === 401) {
          // Monthly limit reached (or signed out elsewhere): full sign-in.
          this.zone.run(() => this.router.navigate(['/logout']));
        }
      }
    );
  }
}
